import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { AgentCard } from '../components/AgentCard';
import { Button } from '../components/ui';
import { AGENTS } from '../lib/agents';
import { useAuth } from '../providers/AuthProvider';

/** Map an agent's display name (e.g. "Ray") to its route id (e.g. "ray"). */
function agentIdForName(name: string): string | undefined {
  return AGENTS.find((a) => a.name.toLowerCase() === name.toLowerCase())?.id;
}

/**
 * Landing page — the first thing visitors see.
 */
export function LandingPage(): React.JSX.Element {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [agentsExpanded, setAgentsExpanded] = useState(false);
  const [tiersExpanded, setTiersExpanded] = useState(false);
  const [guestTierExpanded, setGuestTierExpanded] = useState(false);
  const [memberTierExpanded, setMemberTierExpanded] = useState(false);
  const [earlyTesterTierExpanded, setEarlyTesterTierExpanded] = useState(false);
  const [practitionerTierExpanded, setPractitionerTierExpanded] = useState(false);

  return (
    <div className="flex flex-col">
      {/* ─── Hero Section ─────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-white px-4 pb-6 pt-8 sm:px-6 sm:pb-8 sm:pt-12 lg:px-8">
        <div className="relative mx-auto max-w-6xl text-center">
          <h1 className="text-3xl font-bold tracking-tight text-text-primary sm:text-4xl lg:text-5xl">
            Make Work Better, Faster &mdash; With Less AI Drift
          </h1>

          {/* ─── Scenarios Section (always open) ──────────────────────── */}
          <div className="mt-8">
            <h3 className="text-center text-lg font-bold text-text-primary">
              AppreciativeAI gives you great answers that make more than perfect logical sense
              <br />
              to help you realize your real opportunities and reduce your real tensions faster.
            </h3>

            <div className="mt-4">
              <p className="mx-auto max-w-3xl text-base text-text-secondary mb-4">
                AI generalist agents often give brilliant answers that don&apos;t quite fit, leaving you
                iterating and second&#8209;guessing. AppreciativeAI reduces this AI Drift by aligning agent
                answers to your chosen perspective — to what you&apos;re sensing, seeing, and actually trying
                to do at work. So, instead of relying on a one&#8209;size&#8209;fits&#8209;all generalist
                answer, your chosen AAI agent — tuned to your current work lens — keeps the conversation
                aligned with your real situation and the future you&apos;re creating, as you see it.
              </p>
              <p className="mx-auto max-w-3xl text-base text-text-secondary mb-4">
                Wherever you are in your work story, you don&apos;t have to figure it out alone — or bend yourself
                around AI answers that don&apos;t fit. You can get clarity, momentum, and next steps you can
                actually trust sooner – with AppreciativeAI.
              </p>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                <ScenarioCard
                  quote="I'm overwhelmed by a new responsibility."
                  agent="Ray"
                  label="Be Ready"
                  colour="#C75B5B"
                  detail="You've been handed a project, a role, or a challenge you didn't see coming. You're capable — but you need clarity, tools, and a place to start. Ray gives you perspective to help you steady the ground under your feet, organize what's in front of you, and build a foundation you can count on."
                  bullets={["You\u2019re stepping into something unfamiliar", "You need structure, not stress", "You want to feel prepared before you act"]}
                />
                <ScenarioCard
                  quote="I can't get motivated at work."
                  agent="Olympia"
                  label="Be Eager"
                  colour="#D4874D"
                  detail="You used to feel energized. Now everything feels heavier than it should. Olympia helps you reconnect with what once fueled you — joy, challenge, curiosity — and rediscover the lightness that makes work enjoyable again."
                  bullets={['You feel stuck', 'You want to feel more excited about work', 'You need a reminder of what makes your work worth doing']}
                />
                <ScenarioCard
                  quote="I'm facing a decision I can't get wrong."
                  agent="Yves"
                  label="Be Steady"
                  colour="#C9A84C"
                  detail="You're weighing options, risks, timelines, and tradeoffs. You want to move forward — but not blindly. Yves helps you gain the perspective you need to slow the noise, think clearly, and choose with confidence."
                  bullets={["You\u2019re navigating uncertainty", "You want grounded, steady thinking", "You need a decision you can stand behind"]}
                />
                <ScenarioCard
                  quote="I'm struggling with someone I work with."
                  agent="Gail"
                  label="Be Connected"
                  colour="#5B9E6F"
                  detail="A relationship at work feels strained, confusing, or distant. You want collaboration, not tension. Gail helps you gain perspective on the dynamics to strengthen connection and rebuild trust."
                  bullets={["You\u2019re dealing with conflict or misalignment", "You want better communication", "You want to feel more like you belong"]}
                />
                <ScenarioCard
                  quote="I feel like I'm fitting myself into a mold."
                  agent="Timothy"
                  label="Be Yourself"
                  colour="#4D9E9E"
                  detail="You're holding back — your ideas, your voice, your perspective. Maybe you're new. Maybe you're unsure. Maybe you're tired of pretending. Timothy helps you gain the perspective you need to express your authentic self and feel grounded in who you are at work."
                  bullets={['You feel invisible or misunderstood', 'You want to speak with confidence', 'You need permission to be yourself at work']}
                />
                <ScenarioCard
                  quote="I need some fresh ideas."
                  agent="Byron"
                  label="Be Open"
                  colour="#4A7FB5"
                  detail="You're stuck in a loop of familiar solutions. You want new angles, new ideas, new possibilities. Byron helps you explore perspectives you haven't considered and expand what you think is possible at work."
                  bullets={['You want to brainstorm or innovate', 'You want to break out of habitual thinking', 'You need a wider lens to make sense of things']}
                />
                <ScenarioCard
                  quote="I'm not sure what I'm contributing really matters."
                  agent="Peter"
                  label="Be Essential"
                  colour="#7B5EA7"
                  detail="You're ready to mentor, guide, or build something lasting. You want your work to matter and live on beyond the moment. Peter helps give you the perspective you need to clarify your contribution and shape the legacy you're building."
                  bullets={['You want to step into leadership', 'You want to elevate others', 'You want purpose, not just productivity']}
                />
              </div>
            </div>
          </div>

          {/* Colour accent line */}
          <div className="mx-auto mt-6 h-1 w-24 rounded-full bg-brand" />
        </div>
      </section>

      {/* ─── Agent Preview Section ────────────────────────────────────── */}
      <section id="agents-section" className="bg-white px-4 pt-4 pb-10 sm:px-6 sm:pt-4 sm:pb-14 lg:px-8">
        <div className="mx-auto max-w-6xl">
          {/* Prism illustration */}
          <div className="mb-6 flex justify-center">
            <svg
              width="260"
              height="120"
              viewBox="0 0 260 120"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
              className="opacity-90"
            >
              {/* White light beam entering prism */}
              <line x1="0" y1="60" x2="85" y2="60" stroke="#1D1C1D" strokeWidth="3" strokeLinecap="round" />
              {/* Prism (triangle) */}
              <polygon points="90,20 150,20 120,100" fill="none" stroke="#1D1C1D" strokeWidth="2" />
              {/* Seven coloured rays exiting prism */}
              <line x1="160" y1="28" x2="260" y2="8" stroke="#C75B5B" strokeWidth="2.5" strokeLinecap="round" />
              <line x1="161" y1="35" x2="260" y2="25" stroke="#D4874D" strokeWidth="2.5" strokeLinecap="round" />
              <line x1="162" y1="42" x2="260" y2="42" stroke="#C9A84C" strokeWidth="2.5" strokeLinecap="round" />
              <line x1="162" y1="49" x2="260" y2="58" stroke="#5B9E6F" strokeWidth="2.5" strokeLinecap="round" />
              <line x1="161" y1="56" x2="260" y2="75" stroke="#4D9E9E" strokeWidth="2.5" strokeLinecap="round" />
              <line x1="160" y1="63" x2="260" y2="92" stroke="#4A7FB5" strokeWidth="2.5" strokeLinecap="round" />
              <line x1="158" y1="70" x2="260" y2="108" stroke="#7B5EA7" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
          </div>

          <div className="mb-10 text-center">
            <h2 className="text-2xl font-bold text-text-primary sm:text-3xl">
              <span>
                <span style={{ color: '#C75B5B' }}>S</span>
                <span style={{ color: '#D4874D' }}>e</span>
                <span style={{ color: '#C9A84C' }}>v</span>
                <span style={{ color: '#5B9E6F' }}>e</span>
                <span style={{ color: '#4D9E9E' }}>n</span>
              </span> AAI Agents, One Complete Picture. Less AI Drift.
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-text-secondary">
              Like white light passing through a prism, each AAI agent reveals a different colour of your situation and
              strengths -- as you see it. Start with the AAI agent that meets you where you are. Or explore all seven to
              grow your perspective into a full picture that fuels your whole situation forward.
            </p>
          </div>

          {/* ─── Start a Conversation (expandable agent grid) ──────────── */}
          <div className="mb-10 rounded-lg bg-[#611F69]/10 p-6 sm:p-8">
            <div className="flex items-center justify-center gap-2">
              <h3 className="text-center text-xl font-bold text-text-primary">
                Start a conversation now:
              </h3>
              <button
                type="button"
                className="text-sm text-text-muted hover:text-text-primary"
                onClick={() => setAgentsExpanded(!agentsExpanded)}
                aria-expanded={agentsExpanded}
              >
                {agentsExpanded ? 'Less ▲' : 'More ▼'}
              </button>
            </div>

            {agentsExpanded && (
              <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {AGENTS.map((agent) => (
                  <AgentCard key={agent.id} agent={agent} size="compact" />
                ))}
              </div>
            )}
          </div>

          {/* ─── Tiered Access Section ─────────────────────────────────── */}
          <div className="mb-10 rounded-lg bg-[#611F69]/10 p-6 sm:p-8">
            <div className="flex items-center justify-center gap-2">
              <h3 className="text-center text-xl font-bold text-text-primary">
                Choose how you want to join us:
              </h3>
              <button
                type="button"
                className="text-sm text-text-muted hover:text-text-primary"
                onClick={() => setTiersExpanded(!tiersExpanded)}
                aria-expanded={tiersExpanded}
              >
                {tiersExpanded ? 'Less ▲' : 'More ▼'}
              </button>
            </div>

            {tiersExpanded && (
              <>
                <p className="mx-auto mt-3 max-w-3xl text-center text-sm text-text-secondary">
                  Collaborate with your own hand-picked team of AI agents on a work issue, initiative, task, or opportunity.
                  Each brings a different lens. Each has your back. Together, helping you see the full picture to fuel yourself
                  forward with gaps bridged, clarity gained, confident steps taken.
                </p>

                <div className="mt-6 space-y-4">
              {/* Guest Access */}
              <TierItem
                title="Guest Access – Free - No Login"
                expanded={guestTierExpanded}
                onToggle={() => setGuestTierExpanded(!guestTierExpanded)}
                whatYouGet={['Have unsaved conversations with all 7 AAI agents']}
                moreContent="Explore your work situation through conversations with any AAI agent. No account needed. Sessions don't save."
              />

              {/* Member Access */}
              <TierItem
                title="Member Access – Free - Login Required"
                expanded={memberTierExpanded}
                onToggle={() => setMemberTierExpanded(!memberTierExpanded)}
                whatYouGet={[
                  'Have unsaved conversations with all 7 AAI agents',
                  'Browse Community discussion posts',
                ]}
                moreContent="Click Join Us to create a free Member account. Explore your work situation with your own hand-picked team of AAI agents (sessions don't save) and gain insights from browsing Community posts made by other professionals."
              />

              {/* Collaborator (Early Tester) Access */}
              <TierItem
                title="Collaborator (Early Tester) Access – Free - Login Required"
                badge="Early Tester Access – Limited Spots Available"
                expanded={earlyTesterTierExpanded}
                onToggle={() => setEarlyTesterTierExpanded(!earlyTesterTierExpanded)}
                whatYouGet={[
                  'Have saved conversations with all 7 AAI agents during a defined testing period',
                  'Gain insight from your own personal Dashboard',
                  'Post in Community discussions',
                  'Help shape the future AAI product',
                ]}
                moreContent="Click Join Us and check the Early Tester box for full, free access throughout the testing-period (August 18 – September 18, 2026). Explore your work situation with saved conversations you can return to anytime. Use your personal Dashboard to revisit sessions, see highlights, and track your journeys with your hand-picked team of AAI agents. Contribute to Community discussions with other professionals."
                note="Early Testers agree that anonymized session data may be used to improve the AAI product after the test period ends. We'll notify you by email and give you one week to copy your data before the testing period ends. After that, your account will convert to regular member access."
              />

              {/* Practitioner Access */}
              <TierItem
                title="Practitioner – Paid - Login Required"
                expanded={practitionerTierExpanded}
                onToggle={() => setPractitionerTierExpanded(!practitionerTierExpanded)}
                whatYouGet={[
                  'Have saved conversations with all 7 AAI agents',
                  'Gain insight from your own personal Dashboard',
                  'Post in Community discussions',
                ]}
                moreContent="After Early Testing (August 18 – September 18, 2026), we will begin to implement this paid option. To pay for this access, you will click Join Us and select the Practitioner membership option. As a Practitioner, you will have full access to AppreciativeAI, exploring your work situation with saved conversations you can return to anytime. Using your personal Dashboard to revisit sessions, see highlights, and track your journeys with your hand-picked team of AAI agents. Contributing to Community discussions with other professionals. As well as any enhancements or additional features resulting from Early Testing feedback and data."
                note="🚧 Under Construction"
              />
            </div>

            <div className="mt-6 flex justify-center">
              <Button
                size="lg"
                className="min-h-[44px] min-w-[44px] px-8 text-base font-semibold shadow-md"
                onClick={() => navigate('/register')}
              >
                Join Us
              </Button>
            </div>
              </>
            )}
          </div>

        </div>
      </section>

      {/* ─── Community Teaser (authenticated users only) ──────────────── */}
      {isAuthenticated && (
        <section className="bg-white px-4 py-16 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-2xl font-bold text-text-primary">
              Connect with others
            </h2>
            <p className="mt-3 text-text-secondary">
              Chat with professionals who share your colour affinity or look for cross-colour
              perspectives to grow what fuels you.
            </p>
            <div className="mt-6">
              <Button size="lg" onClick={() => navigate('/community')}>
                Explore the Community
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* ─── Footer: Value Pillars ────────────────────────────────────── */}
      <section className="border-t border-border bg-surface-muted px-4 py-12 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl">
          <div className="grid gap-8 sm:grid-cols-3">
            <ValuePillar
              title="Start where you are"
              description="Appreciate the full worth and potential of your present work situation."
            />
            <ValuePillar
              title="Use what you have"
              description="Understand real-time implications, constraints, and resources available to you."
            />
            <ValuePillar
              title="Do what you can"
              description="Identify meaningful actions you can take right now to move forward."
            />
          </div>
        </div>
      </section>
    </div>
  );
}

/* ─── Helpers ─────────────────────────────────────────────────────────── */

function ValuePillar({ title, description }: { title: string; description: string }) {
  return (
    <div className="flex flex-col items-center text-center sm:items-start sm:text-left">
      <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-full bg-white text-gail">
        <CheckIcon />
      </div>
      <h3 className="text-base font-semibold text-text-primary">{title}</h3>
      <p className="mt-1.5 text-sm text-text-secondary">{description}</p>
    </div>
  );
}

function CheckIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

/* ─── Tier Item (expandable) ──────────────────────────────────────────── */

function TierItem({
  title,
  badge,
  expanded,
  onToggle,
  whatYouGet,
  moreContent,
  note,
}: {
  title: string;
  badge?: string;
  expanded: boolean;
  onToggle: () => void;
  whatYouGet: string[];
  moreContent: string;
  note?: string;
}) {
  return (
    <div className="rounded-lg border border-border bg-white p-4">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="text-sm font-bold text-text-primary">{title}</h4>
            {badge && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[#611F69] px-3 py-1 text-xs font-semibold text-white">
                <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-white" />
                {badge}
              </span>
            )}
          </div>
          <div className="mt-2">
            <p className="text-xs font-semibold text-text-muted uppercase tracking-wide">What you get:</p>
            <ul className="mt-1 space-y-1">
              {whatYouGet.map((item, i) => (
                <li key={i} className="flex items-start gap-1.5 text-sm text-text-secondary">
                  <span className="text-[#611F69]">•</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
        <button
          type="button"
          className="ml-3 flex-shrink-0 text-xs text-text-muted hover:text-text-primary"
          onClick={onToggle}
          aria-expanded={expanded}
        >
          {expanded ? 'Less ▲' : 'More ▼'}
        </button>
      </div>

      {expanded && (
        <div className="mt-3 border-t border-border pt-3">
          <p className="text-sm text-text-secondary leading-relaxed">{moreContent}</p>
          {note && (
            <p className="mt-2 text-xs text-text-muted italic leading-relaxed">
              Note: {note}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Scenario Card (expandable on click) ─────────────────────────────── */

function ScenarioCard({
  quote,
  agent,
  label,
  colour,
  detail,
  bullets,
}: {
  quote: string;
  agent: string;
  label: string;
  colour: string;
  detail: string;
  bullets: string[];
}) {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();
  const agentId = agentIdForName(agent);

  return (
    <div
      className="cursor-pointer rounded-lg border border-border bg-white p-4 transition hover:shadow-md"
      style={{ borderLeftWidth: '4px', borderLeftColor: colour }}
      onClick={() => setExpanded(!expanded)}
      role="button"
      aria-expanded={expanded}
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setExpanded(!expanded); }}
    >
      {/* Quote */}
      <p className="text-sm font-medium text-text-primary italic">&ldquo;{quote}&rdquo;</p>

      {/* Agent + label */}
      <p className="mt-2 text-xs font-bold" style={{ color: colour }}>
        Let {agent} Help You – {label}
      </p>

      {/* Expanded detail */}
      {expanded && (
        <div className="mt-3 border-t border-border pt-3">
          <p className="text-xs text-text-secondary leading-relaxed">{detail}</p>
          <ul className="mt-2 space-y-1">
            {bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-1.5 text-xs text-text-muted">
                <span style={{ color: colour }}>•</span>
                {b}
              </li>
            ))}
          </ul>

          {/* Start a Conversation link */}
          {agentId && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/agents/${agentId}/chat`);
              }}
              className="mt-3 w-full rounded-md py-2 text-xs font-semibold text-white transition hover:opacity-90"
              style={{ backgroundColor: colour }}
            >
              Start a Conversation with {agent}
            </button>
          )}
        </div>
      )}

      {/* Expand/collapse arrow */}
      <span className="mt-2 block text-center text-xs text-text-muted">
        {expanded ? '\u25B2' : '\u25BC'}
      </span>
    </div>
  );
}
